import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  useUpdatePlaybook,
  type AdminPlaybookRow,
  type PlaybookStatus,
  type UpdatePlaybookBody,
} from "@/queries/adminPlaybooks";

const EDITABLE_STATUSES: PlaybookStatus[] = ["active", "paused", "deprecated"];

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  playbook: AdminPlaybookRow;
}

export function PlaybookEditDialog({ open, onOpenChange, playbook }: Props) {
  const { t } = useTranslation("admin-playbooks");
  const mutation = useUpdatePlaybook(playbook.id);

  const [statusVal, setStatusVal] = useState<PlaybookStatus>(playbook.status);
  const [liveAllowed, setLiveAllowed] = useState<boolean>(playbook.live_allowed);
  const [sizeOverride, setSizeOverride] = useState<string>(
    playbook.position_size_override ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStatusVal(playbook.status);
    setLiveAllowed(playbook.live_allowed);
    setSizeOverride(playbook.position_size_override ?? "");
    setError(null);
  }, [playbook.id, playbook.status, playbook.live_allowed, playbook.position_size_override]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const body: UpdatePlaybookBody = {};
    if (statusVal !== playbook.status) body.status = statusVal;
    if (liveAllowed !== playbook.live_allowed) body.live_allowed = liveAllowed;

    const trimmed = sizeOverride.trim();
    const previousAsString = playbook.position_size_override ?? "";
    if (trimmed !== previousAsString) {
      if (trimmed === "") {
        body.position_size_override = null;
      } else {
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) {
          setError(t("errors.notNumeric"));
          return;
        }
        if (parsed <= 0 || parsed > 1) {
          setError(t("errors.outOfRange"));
          return;
        }
        body.position_size_override = parsed;
      }
    }

    if (Object.keys(body).length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      await mutation.mutateAsync(body);
      onOpenChange(false);
    } catch {
      setError(t("errors.saveGeneric"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {t("edit.title", { name: playbook.display_name })}
            </DialogTitle>
            <DialogDescription>{t("edit.subtitle")}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="playbook-status">{t("edit.status")}</Label>
              <Select
                id="playbook-status"
                value={statusVal}
                onChange={(e) => setStatusVal(e.target.value as PlaybookStatus)}
              >
                {EDITABLE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`status.${s}`)}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">{t("edit.statusHint")}</p>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="playbook-live-allowed">{t("edit.liveAllowed")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("edit.liveAllowedHint")}
                </p>
              </div>
              <Switch
                id="playbook-live-allowed"
                checked={liveAllowed}
                onCheckedChange={setLiveAllowed}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="playbook-size-override">
                {t("edit.positionSizeOverride")}
              </Label>
              <Input
                id="playbook-size-override"
                inputMode="decimal"
                placeholder="0.05"
                value={sizeOverride}
                onChange={(e) => setSizeOverride(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t("edit.positionSizeOverrideHint")}
              </p>
            </div>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t("edit.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("edit.saving") : t("edit.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
